import { Show } from "components/core";
import { FC, useEffect, useMemo, useRef } from "react";

import { ChatBubbleLeftEllipsisIcon } from "@heroicons/react/24/outline";
import { FormikProps } from "formik";
import _, { get } from "lodash";
import { useCurrentUser } from "_graphql/cache/auth";
import { Send } from "lucide-react";
import { wrapClick } from "utils";
import { calendarTime } from "utils/date";

type Props = {
  offers: Record<string, any>[];
  form: FormikProps<any>;
  hideInput?: boolean;
  sending?: boolean;
};

const MessageView: FC<Props> = ({ offers, form, hideInput, sending }) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const currentUser = useCurrentUser();

  const currentUserName = useMemo(() => {
    const first = currentUser?.employee?.employee_first_name || "";
    const last = currentUser?.employee?.employee_last_name || "";
    return [first, last].join(" ").trim().toLowerCase();
  }, [currentUser]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "end",
    });
  };

  useEffect(() => {
    scrollToBottom();
  }, [offers]);

  return (
    <div className="flex flex-col flex-1 h-[500px] overflow-clip">
      <div className="flex h-full flex-1 justify-between overflow-y-scroll no-scrollbar flex-col">
        {offers?.length < 1 ? (
          <div className="flex-1 h-full flex flex-col justify-center items-center space-y-2">
            <ChatBubbleLeftEllipsisIcon className="h-12 w-12 text-gray-400" />
            <p className="text-gray-500 font-medium">No messages yet</p>
          </div>
        ) : (
          <div className="flex flex-col h-screen bg-gray-50">
            {/* Messages Container */}
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              {offers.map((message) => (
                <div
                  key={message?.uuid}
                  className={`flex ${message?.sender_name?.toLowerCase() === currentUserName ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-xs lg:max-w-md px-4 py-2 rounded-2xl ${
                      message?.sender_name?.toLowerCase() === currentUserName
                        ? "bg-blue-100 text-blue-600 rounded-br-none"
                        : "bg-white text-gray-800 rounded-bl-none shadow-sm border border-gray-100"
                    }`}
                  >
                    <p className="text-xs font-light italic">
                      {message?.sender_name}{" "}
                      <Show if={Boolean(message?.source)}>
                        <span>({message?.source})</span>
                      </Show>
                    </p>
                    <p
                      className="text-sm whitespace-pre-wrap break-words"
                      dangerouslySetInnerHTML={{
                        __html: message?.message || "",
                      }}
                    ></p>
                    <p
                      className={`text-xs mt-1 ${
                        message?.sender_name?.toLowerCase() === currentUserName
                          ? "text-blue-500"
                          : "text-gray-500"
                      }`}
                    >
                      {calendarTime(message?.timestamp)}
                    </p>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
          </div>
        )}
      </div>
      <Show if={!hideInput}>
        <div className="flex w-full pt-4 "></div>
        {/* Input Area */}
        <div className="bg-white border-t border-gray-200 px-6 py-4">
          <form className="flex items-center space-x-3">
            <div className="flex-1 flex items-center bg-gray-100 rounded-2xl px-4 py-2">
              <textarea
                name="message"
                id="message"
                value={get(form.values, "message", "")}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                placeholder="Type your message..."
                className="flex-1 bg-transparent border-none focus:outline-none outline-none px-3 py-1 text-gray-800 placeholder-gray-500"
              />
            </div>
            <button
              type="submit"
              disabled={!get(form.values, "message", "").trim() || sending}
              onClick={wrapClick(() => {
                form.handleSubmit();
              })}
              className="bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white p-3 rounded-full transition-colors shadow-lg"
            >
              <Show if={!sending}>
                <Send className="w-5 h-5" />
              </Show>
              <Show if={Boolean(sending)}>
                <svg
                  className="size-5 animate-spin text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                  ></path>
                </svg>
              </Show>
            </button>
          </form>
        </div>
      </Show>
    </div>
  );
};

export default MessageView;
